import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { TextPlugin } from "gsap/dist/TextPlugin";
import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import VariableImage from "./ui/VariableImage";

gsap.registerPlugin(useGSAP, ScrollTrigger, ScrollToPlugin, TextPlugin);

const VerticalSlider = ({
  data,
  className,
}: {
  data: any;
  className?: string;
}) => {
  const homeVerticalSliderRef = useRef<HTMLDivElement>(null);
  const verticalPinTl = useRef<any>(null);
  const masterTl = useRef<any>(null);
  const media = useRef<any>(null);
  const breakPoint = 1024;
  const [pinDuration, setPinDuration] = useState<number>(0);
  const [activeSlider, setActiveSlider] = useState<boolean>(false);
  // `data` is the homepage object (`SectionThree*`) or a campaign block
  // (`VerticalSlider*`). Pick by which field is present, not the
  // `isCampaignPage` store: that store is unset during static export
  // (`output: export`), and a wrong guess runs `.map` on `undefined` below and
  // crashes the prerender, failing the whole build.
  const title = data?.VerticalSliderTitle ?? data?.SectionThreeTitle;
  const sectionData = data?.VerticalSliderData ?? data?.SectionThree ?? [];
  const [slideHeads, setSlideHeads] = useState<HTMLElement[]>([]);
  const [slideImgs, setSlideImgs] = useState<HTMLImageElement[]>([]);

  useGSAP(
    () => {
      media.current = gsap.matchMedia();
      media.current.add(`(min-width: ${breakPoint}px)`, () => {
        setPinDuration(sectionData.length * 1000);
      });
      media.current.add(`(max-width: ${breakPoint}px)`, () => {
        setPinDuration(sectionData.length * 500);
      });

      setSlideHeads(gsap.utils.toArray("#homeVerticalSlider .slide-head"));
      setSlideImgs(gsap.utils.toArray("#homeVerticalSlider .slide-img"));
      setActiveSlider(sectionData.length > 1);
    },
    { scope: homeVerticalSliderRef, dependencies: [sectionData] },
  );

  const { contextSafe } = useGSAP(
    () => {
      masterTl.current = gsap.timeline();

      //   the animation for the section intro
      const scrollTl = gsap
        .timeline({
          scrollTrigger: {
            trigger: homeVerticalSliderRef.current,
            start: "top 70%",
            end: "+=400",
            scrub: 1,
          },
        })
        .from("#homeVerticalSlider .fade-intro", {
          autoAlpha: 0,
          y: 50,
          stagger: 0.1,
        })
        .from(
          "#homeVerticalSlider .width-intro",
          {
            width: 0,
          },
          "<",
        );

      if (pinDuration && activeSlider) {
        //   the animation for the slides
        verticalPinTl.current = gsap.timeline({
          scrollTrigger: {
            trigger: "#homeVerticalSlider",
            start: "center center",
            end: `+=${pinDuration}`,
            scrub: 1,
            pin: true,
            pinSpacing: "margin",
            invalidateOnRefresh: true,
            onUpdate: (e) => {
              if (sectionData.length > 1) {
                gsap.set("#progress", {
                  width: `${Number(e.progress.toFixed(3)) * 100}%`,
                });
              }
            },
          },
        });

        const lastIndex = slideHeads.length - 1;
        slideHeads.forEach((head: any, i: number) => {
          verticalPinTl.current.to([head, slideImgs[i]], {
            opacity: 1,
          });

          verticalPinTl.current.to(
            "#slideNumber",
            {
              text: (i + 1).toString(),
            },
            "<",
          );

          verticalPinTl.current.addLabel(`label-${i}`);

          verticalPinTl.current.to([head], {
            opacity: i === lastIndex ? 1 : 0,
          });
        });

        masterTl.current.add(scrollTl);
        masterTl.current.add(verticalPinTl.current);
      }
    },
    {
      dependencies: [activeSlider],
      scope: homeVerticalSliderRef,
      revertOnUpdate: true,
    },
  );

  useGSAP(
    () => {
      if (!homeVerticalSliderRef.current && !pinDuration && !activeSlider)
        return;
      ScrollTrigger.create({
        trigger: homeVerticalSliderRef.current,
        start: "center center",
        end: `+=${pinDuration}`,
        scrub: 1,
        pin: "#mainBg",
        pinSpacing: false,
        refreshPriority: -1,
      });
    },
    {
      dependencies: [activeSlider],
      revertOnUpdate: true,
    },
  );

  return (
    <div
      ref={homeVerticalSliderRef}
      className={cn("relative pb-10", className)}
    >
      {title && <h2 className="title-50 mb-20">{title}</h2>}

      <div id="homeVerticalSlider" className="relative ">
        <div className="mt-0 mb-10 lg:my-20 xl:h-fit ">
          <div className="width-intro seperator border-black pb-5"></div>
          <div className="fade-intro flex h-full flex-wrap items-start justify-between space-y-20 lg:items-end lg:space-y-10">
            <div className="relative">
              {sectionData.map((slide: any, i: number) => (
                <div
                  key={i}
                  className={cn(
                    "slide-head relative grid space-y-5 lg:grid-cols-2 lg:gap-20",
                    i !== 0 && "absolute top-0 opacity-0",
                  )}
                >
                  <h2 className="title-50 top-0 w-full">{slide.Title}</h2>
                  <p className="whitespace-pre-line">{slide.Description}</p>
                </div>
              ))}
            </div>

            {sectionData.length > 1 && (
              <div className="lg:ml-auto lg:basis-1/2">
                <div className="flex items-center justify-between gap-5">
                  <div>
                    <div>
                      <span id="slideNumber">1</span>/{sectionData.length}
                    </div>
                    <div className="relative h-[2px] w-20 bg-[#c5c0be]">
                      <span
                        id="progress"
                        className="absolute h-full w-1/4 bg-black"
                      ></span>
                    </div>
                  </div>
                  {/* <div className="flex items-center gap-5 ">
                  <button
                    onClick={prevSlide}
                    id="prevBtn"
                    className="size-10 lg:size-16 rtl:rotate-180"
                  >
                    <Arrow fill="#000" />
                  </button>
                  <button
                    onClick={nextSlide}
                    id="nextBtn"
                    className="size-10 lg:size-16 rtl:rotate-180"
                  >
                    <Arrow arrowType="next" fill="#000" />
                  </button>
                </div> */}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="fade-intro relative max-sm:aspect-video lg:mx-5">
          {sectionData.map((slide: any, i: number) => {
            const img = slide.Image?.data?.attributes;
            const video = slide.Video?.data?.attributes?.url;
            return (
              <React.Fragment key={i}>
                {video && (
                  <video
                    key={i}
                    src={video}
                    className={cn(
                      "slide-img size-full object-cover",

                      i !== 0 && "absolute inset-0 opacity-0",
                    )}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                  />
                )}
                {img && !video && (
                  <VariableImage
                    src={img.url}
                    width={1920}
                    height={1080}
                    className={cn(
                      "slide-img size-full object-cover",

                      i !== 0 && "absolute inset-0 opacity-0",
                    )}
                    alt={img.alternativeText}
                    caption={img.caption}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VerticalSlider;
